import { PlugZap } from "lucide-react";

import { saasDestinations } from "@/destinations/destinations";
import { WizardDefinition } from "../framework/types";
import { wizardMascots } from "../mascots/mascots";
import { getAllowlistCandidates } from "./allowlist";
import { getEnableDestinationSummary } from "./completionSummary";
import { ENABLE_DESTINATION_WIZARD_TYPE } from "./constants";
import { isDeliveryStepApplicable } from "./delivery";
import { AllowlistStep } from "./steps/AllowlistStep";
import { ChooseDestinationStep } from "./steps/ChooseDestinationStep";
import { ChooseEventsStep } from "./steps/ChooseEventsStep";
import { ConfirmInDestinationStep } from "./steps/ConfirmInDestinationStep";
import { ConnectAccountStep } from "./steps/ConnectAccountStep";
import { ConsentCategoryStep } from "./steps/ConsentCategoryStep";
import { DeliveryStep } from "./steps/DeliveryStep";
import { LiveViewStep } from "./steps/LiveViewStep";
import { TransformationsStep } from "./steps/TransformationsStep";
import { TurnOnStep } from "./steps/TurnOnStep";
import {
  EnableDestinationContext,
  EnableDestinationData,
  EnableDestinationParams,
} from "./types";
import { useEnableDestinationContext } from "./useEnableDestinationContext";

export function isAllowlistStepApplicable(
  _data: EnableDestinationData,
  {
    isHipaaV2,
    saasDestination,
    configuredDestination,
  }: EnableDestinationContext,
): boolean {
  if (!isHipaaV2 || configuredDestination?.has_baa) {
    return false;
  }
  // Until a destination is chosen we can't tell, so keep the step in the
  // outline rather than having it appear later.
  if (!saasDestination) {
    return true;
  }
  return (
    saasDestination.supportsServerSide &&
    getAllowlistCandidates(saasDestination.key).length > 0
  );
}

function getDestinationName(data: EnableDestinationData): string | undefined {
  return data.destinationSlug
    ? saasDestinations[data.destinationSlug as keyof typeof saasDestinations]
        ?.name
    : undefined;
}

export const enableDestinationWizard: WizardDefinition<
  EnableDestinationData,
  EnableDestinationContext,
  EnableDestinationParams
> = {
  type: ENABLE_DESTINATION_WIZARD_TYPE,
  title: "Enable a destination",
  description: "Connect a destination and confirm events reach it",
  Icon: PlugZap,
  mascot: wizardMascots[ENABLE_DESTINATION_WIZARD_TYPE],
  searchKeys: ["destination", "integration", "connect", "set up", "setup"],
  createInitialData: (params) => ({
    destinationSlug: params?.destinationSlug ?? null,
    destinationConfigId: null,
    credentialDraft: {},
    eventToggles: {},
  }),
  getSessionKey: (data) => data.destinationSlug ?? undefined,
  getSubtitle: getDestinationName,
  getCompletionSummary: (data, context) =>
    getEnableDestinationSummary(getDestinationName(data), data, context),
  sanitizeForStorage: (data) => ({ ...data, credentialDraft: {} }),
  useStepContext: useEnableDestinationContext,
  steps: [
    {
      id: "choose",
      phase: "Connect",
      title: "Choose destination",
      Component: ChooseDestinationStep,
    },
    {
      id: "connect",
      phase: "Connect",
      title: "Connect account",
      Component: ConnectAccountStep,
    },
    {
      id: "allowlist",
      phase: "Protect",
      title: "Allowlist properties",
      Component: AllowlistStep,
      isApplicable: isAllowlistStepApplicable,
    },
    {
      id: "consent",
      phase: "Protect",
      title: "Categorize for consent",
      Component: ConsentCategoryStep,
      isApplicable: (_data, context) => context.activeConsentManager !== null,
    },
    {
      id: "events",
      phase: "Configure",
      title: "Choose events",
      Component: ChooseEventsStep,
    },
    {
      id: "transformations",
      phase: "Configure",
      title: "Add transformations",
      Component: TransformationsStep,
    },
    {
      id: "turn-on",
      phase: "Go live",
      title: "Turn on",
      Component: TurnOnStep,
    },
    {
      id: "live-view",
      phase: "Validate",
      title: "Check events are firing",
      Component: LiveViewStep,
    },
    {
      id: "delivery",
      phase: "Validate",
      title: "Check delivery",
      Component: DeliveryStep,
      isApplicable: (_data, context) => isDeliveryStepApplicable(context),
    },
    {
      id: "confirm",
      phase: "Validate",
      title: "Confirm in destination",
      Component: ConfirmInDestinationStep,
    },
  ],
};
